import { Alert, Backdrop, Box, Tabs } from '@mui/material';
import { checkEmployeeEmailAndInszExistRequest } from 'api/requests/employee-requests';
import UPButton from 'components/button';
import { UPDropdownItem } from 'components/dropdown';
import MessageModal from 'components/message-modal';
import { TabStyled } from 'components/tab/styles';
import { CompanyRole } from 'constants/CompanyRole';
import { EmployeeExistsResponse } from 'constants/EmployeeExistsResponse';
import { EmployeeStatus } from 'constants/EmployeeStatus';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { isNaN, toNumber } from 'lodash';
import { fromDocumentToDocumentToSend } from 'modules/documents/helpers';
import { Document, DocumentToSend } from 'modules/documents/types';
import dayjs from 'utils/dayjs';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Element, scroller } from 'react-scroll';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getDocumentsAction,
  saveDocumentsAction,
  updateStudentDeclarationAction,
  uploadStudentInscription,
} from 'store/actions/documents-action';
import {
  checkEmployeeInvitationTokenValidity,
  clearEmployeeAction,
  fetchAndPrepareFile,
  getEmployeeByIdAction,
  getExistingEmployeeToken,
  saveEmployeeAction,
  updateEmployeeAction,
} from 'store/actions/employee-actions';
import { clearBicAction } from 'store/actions/financial-actions';
import { deleteMfaOverrideAction } from 'store/actions/user-mfa-actions';
import { employeeDocumentsSelector } from 'store/selectors/documents-selectors';
import {
  employeeSelector,
  employeesErrorSelector,
  existingEmployeeInfoAtOnboardingSelector,
  isOnboardingExistingEmployeeSelector,
  onboardingDetailsSelector,
  onboardingSelector,
  onboardingWithGeneralLinkSelector,
  setInviteTokenValiditySelector,
} from 'store/selectors/employee-selector';
import { bicStateSelector } from 'store/selectors/financial-selectors';
import { hasSelectedCompanySelector, profileSelector } from 'store/selectors/profile-selectors';
import useCanWriteMfaOverride from 'hooks/useCanWriteMfaOverride';
import { getMfaOverrideToSend, shouldClearMfaOverride } from 'utils/mfa-override';
import theme from 'theme';
import {
  ACTION__APPROVE,
  ACTION__NOT_ELIGIBLE,
  CONTRACTS__DEFAULT_ERROR,
  EMPLOYEES__ACCOUNT,
  EMPLOYEES__ADD_NEW_EMPLOYEE,
  EMPLOYEES__ADDED_SUCCESSFULLY,
  EMPLOYEES__CONTACT_INFO,
  EMPLOYEES__EDIT_EMPLOYEE,
  EMPLOYEES__EMPLOYMENT,
  EMPLOYEES__IBAN_ERROR,
  EMPLOYEES__INSS_ERROR,
  EMPLOYEES__INVATION_TOKEN_INVALID,
  EMPLOYEES__PERMANENT_CHANGE_VALIDATION,
  EMPLOYEES__PERSONAL_INFO,
  EMPLOYEES__VIEW_EMPLOYEE,
  EMPLOYEES__WORKING_INFO,
  EXISTING_EMPLOYEE_ONBOARDING__LOGIN_ERROR,
  GENERAL__CANCEL_BUTTON,
  GENERAL__GO,
  GENERAL__SAVE_BUTTON,
} from 'translations/constants';
import Lang from 'types/Lang';
import { ICompanyAddress } from 'types/company';
import { IAddEditEmployee, IEmployeeDependants } from 'types/employee';
import MfaRequiredOverride from 'types/MfaRequiredOverride';
import { getUpDropdownItemsWithIdAsValueFromEnum } from 'utils/helpers';
import { AccountTab, ContactInfoTab, WorkingInfoTab } from '.';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import {
  generateEmployeeDocumentForPeriodUpdate,
  isAccountError,
  isContactInfoError,
  isEmploymentError,
  isIdPeriodValid,
  isINSSValid,
  isPersonalInfoError,
  isWorkingInfoError,
  treatAsUTC,
} from '../helpers';
import EligibilityAndTerminationDetails from './EligibilityAndTerminationDetails';
import EmployeeModalMenu from './EmployeeModalMenu';
import OnboardingLogin from './OnboardingLogin';
import PersonalInfoTab from './PersonalInfoTab';
import {
  ContentContainer,
  ModalCloseIcon,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalWrapper,
  TabsContainer,
} from './styles';
import { IAddEditEmployeeModalProps } from './types';

const AddressInitialState: ICompanyAddress = {
  id: 0,
  streetName: '',
  streetNumber: '',
  busNumber: '',
  city: '',
  postalCode: '',
  countryId: '',
};

const EmployeeDependantsInitialState: IEmployeeDependants = {
  id: 0,
  partner: false,
  disabledPartner: false,
  children: '',
  disabledChildren: '',
  elderly: '',
};

const EmployeeInitialState: IAddEditEmployee = {
  id: 0,
  firstName: '',
  lastName: '',
  dateOfBirth: null,
  birthplace: '',
  birthCountry: '',
  nationality: '',
  inss: '',
  languageId: '',
  gender: '',
  workerclass: '',
  reasonForEmployment: '',
  iban: '',
  bic: '',
  idCardNumber: '',
  modeOfTransport: '',
  maritalStatus: null,
  hasDependants: false,
  dependants: EmployeeDependantsInitialState,
  mobileNumber: '',
  emailAddress: '',
  iceMobileNumber: '',
  idUpload: null,
  idUploadURL: '',
  idUploadBack: null,
  idUploadBackURL: '',
  idPeriodStart: null,
  idPeriodEnd: null,
  agreeGdpr: false,
  agreePictureAgreement: false,
  letterOfIntentCheck: false,
  academicPeriodStart: null,
  academicPeriodEnd: null,
  pictureUpload: null,
  pictureUploadURL: '',
  pictureBase64: '',
  password: '',
  address: AddressInitialState,
  hasStayAddress: false,
  stayAddress: AddressInitialState,
  yearsOfExperience: '',
  status: null,
  permanent: false,
  startDate: null,
  companyLocationIds: [],
  canSkipApproval: false,
  retired: false,
  retiredDate: null,
  terminated: false,
  terminatedDate: null,
  eligible: false,
  mfaRequiredOverride: null,
};

const workerClassObj = ['STUD', 'FLEX', 'EXT', 'ARB', 'BED'];

const AddEditEmployeeModal = (props: IAddEditEmployeeModalProps) => {
  const { isEdit, open, onClose, employeeId, companyId, pendingEmployeeCompanyId, onSavePendingEmployee } = props;

  const [t, i18next] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyToken, inviteToken } = useParams();
  const { generalToken } = useParams();
  const isEmployee = useAppSelector(profileSelector)?.role === CompanyRole.EMPLOYEE;
  const onboardingDetails = useAppSelector(onboardingDetailsSelector);
  const employeeOnboarding = useAppSelector(onboardingSelector);
  const employeeOnboardingWithGeneralLink = useAppSelector(onboardingWithGeneralLinkSelector);
  const employeeBic = useAppSelector(bicStateSelector);
  const employeeDocuments = useAppSelector(employeeDocumentsSelector);
  const employee = useAppSelector(employeeSelector);
  const profile = useAppSelector(profileSelector);
  const hasSelectedCompany = useAppSelector(hasSelectedCompanySelector);
  const isAdmin = profile?.role === CompanyRole.ADMIN;
  const canWriteMfaOverride = useCanWriteMfaOverride();
  // This modal is reachable by every role, including the employee editing their own profile, and it
  // has several save paths. A value the caller may not write is kept out of the form altogether
  // rather than stripped in each handler - any one path echoing it back would 403 the whole save.
  const readableMfaOverride = canWriteMfaOverride ? employee?.mfaRequiredOverride ?? null : undefined;
  const isManagerEdit =
    [CompanyRole.COMPANY_MANAGER, CompanyRole.LOCATION_MANAGER, CompanyRole.HR_MANAGER].includes(profile?.role) &&
    employee?.id > 0;
  // its own tab, since the Account tab is hidden from managers
  const canSeeEmployment = (isAdmin || isManagerEdit) && isEdit;

  const [successfullyOnboarded, setSuccessfullyOnboarded] = useState<boolean>(false);
  const [docToUpdate, setDocToUpdate] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState(0);
  const [inssError, setInssError] = useState<boolean>(false);
  const [proofOfInscription, setProofOfInscription] = useState<Document>(null);
  const [newDocToAdd, setNewDocToAdd] = useState<boolean>(false);
  const [isBranchDropdownOpen, setIsBranchDropdownOpen] = useState<boolean>(false);
  const [openOnboardingLogin, setOpenOnboardingLogin] = useState<boolean>(true);
  const [employeeExists, setEmployeeExists] = useState(EmployeeExistsResponse.DOES_NOT_EXIST);
  const [loginFailed, setLoginFailed] = useState<boolean>(false);

  const employeeError = useAppSelector(employeesErrorSelector);
  const isTokenInvalid = useAppSelector(setInviteTokenValiditySelector);
  const existingEmployeeInfo = useAppSelector(existingEmployeeInfoAtOnboardingSelector);
  const isExistingEmployeeOnboarding = useAppSelector(isOnboardingExistingEmployeeSelector);
  const employeeErrorMessages = useMemo(() => {
    return {
      default: t(CONTRACTS__DEFAULT_ERROR),
      inss: t(EMPLOYEES__INSS_ERROR),
      iban: t(EMPLOYEES__IBAN_ERROR),
      permanent: t(EMPLOYEES__PERMANENT_CHANGE_VALIDATION),
    };
  }, [t]);

  const languageOptions: UPDropdownItem[] = useMemo(() => {
    return getUpDropdownItemsWithIdAsValueFromEnum(Lang);
  }, []);

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors, isDirty, isValid, isSubmitting },
  } = useForm<any>({
    defaultValues: EmployeeInitialState,
    mode: 'onChange',
  });

  const busNumberValue = watch('address.busNumber');
  const watchAcademicPeriodStart = watch('academicPeriodStart');
  const watchAcademicPeriodEnd = watch('academicPeriodEnd');
  const watchEmail = watch('emailAddress');
  const watchInss = watch('inss');
  const watchPassword = watch('password');

  const scrollTo = (elementId: string) => {
    scroller.scrollTo(elementId, {
      duration: 500,
      smooth: true,
      containerId: 'tabsContainer',
    });
  };

  const debouncedEmail = useDebouncedValue(watchEmail, 1000);
  const debouncedInss = useDebouncedValue(watchInss, 1000);
  const debouncedPassword = useDebouncedValue(watchPassword, 1000);

  useEffect(() => {
    const checkExistingEmployee = async () => {
      try {
        const doesEmployeeExist = await checkEmployeeEmailAndInszExistRequest(
          employeeOnboarding ? companyToken : companyId.toString(),
          debouncedEmail,
          debouncedInss,
        );
        setEmployeeExists(doesEmployeeExist);
      } catch (error) {
        console.error('Error checking employee existence:', error);
      }
    };

    if (
      !employee &&
      !isEdit &&
      (!employeeOnboarding || employeeOnboardingWithGeneralLink) &&
      debouncedEmail &&
      debouncedInss
    ) {
      checkExistingEmployee();
    }
  }, [
    debouncedEmail,
    debouncedInss,
    isEdit,
    onboardingDetails,
    companyId,
    employeeOnboarding,
    companyToken,
    employeeOnboardingWithGeneralLink,
    employee,
  ]);

  useEffect(() => {
    if (employeeExists !== EmployeeExistsResponse.EXISTS_MATCH) {
      setLoginFailed(false);
      clearErrors('password');
    }
  }, [clearErrors, employeeExists]);

  useEffect(() => {
    async function tryLogin() {
      const loginResult = await dispatch(
        getExistingEmployeeToken({ email: debouncedEmail, inss: debouncedInss, password: debouncedPassword }),
      ).unwrap();
      if (loginResult) {
        setLoginFailed(false);
      } else {
        setLoginFailed(true);
        setError('password', { type: 'invalidPassword', message: t(EXISTING_EMPLOYEE_ONBOARDING__LOGIN_ERROR) });
      }
    }
    if (
      employeeOnboardingWithGeneralLink &&
      employeeExists === EmployeeExistsResponse.EXISTS_MATCH &&
      debouncedInss &&
      debouncedEmail &&
      debouncedPassword &&
      !isExistingEmployeeOnboarding
    ) {
      tryLogin();
    }
  }, [
    employeeOnboardingWithGeneralLink,
    dispatch,
    generalToken,
    employeeExists,
    debouncedInss,
    debouncedEmail,
    debouncedPassword,
    isExistingEmployeeOnboarding,
    setError,
    t,
  ]);

  useEffect(() => {
    const fetchAndSetFile = async (imageName: string) => {
      return dispatch(
        fetchAndPrepareFile({
          companyId: existingEmployeeInfo.companyId,
          employeeId: existingEmployeeInfo.employeeId,
          imageName,
        }),
      );
    };

    if (employeeOnboarding && employee && existingEmployeeInfo) {
      if (employee.idUpload === null && employee.idUploadURL !== '') {
        fetchAndSetFile(employee.idUploadURL).then(res => {
          setValue('idUpload', res.payload);
        });
      }
      if (employee.idUploadBack === null && employee.idUploadBackURL !== '') {
        fetchAndSetFile(employee.idUploadBackURL).then(res => {
          setValue('idUploadBack', res.payload);
        });
      }
      if (employee.pictureUpload === null && employee.pictureUploadURL !== '') {
        fetchAndSetFile(employee.pictureUploadURL).then(res => {
          setValue('pictureUpload', res.payload);
        });
      }
      if (employeeDocuments.length > 0) {
        fetchAndSetFile(employeeDocuments[0].name).then(res => {
          const newDocument: Document = {
            file: res.payload as File,
            id: 1,
            name: employeeDocuments[0].name,
            type: 6,
            error: {
              dateError: false,
              typeError: false,
              invalidDate: false,
            },
          };
          setProofOfInscription(newDocument);
          setNewDocToAdd(true);
          setValue('academicPeriodStart', employeeDocuments[0].periodStart);
          setValue('academicPeriodEnd', employeeDocuments[0].periodEnd);
        });
      }
    }
  }, [employeeOnboarding, employee, dispatch, setValue, existingEmployeeInfo, employeeDocuments, getValues]);

  useEffect(() => {
    if (employeeOnboarding && inviteToken) {
      dispatch(checkEmployeeInvitationTokenValidity({ invitationToken: inviteToken, companyToken }));
    }
  }, [employeeOnboarding, companyToken, inviteToken, dispatch]);

  const handleRequestResponse = useCallback(
    response => {
      if (response === 'invalidEmail') {
        setError('invalidEmail', { type: 'emailSameAsOtherUser' });
        scrollTo('contactInfo');
        return false;
      }

      if (response === 'inss_exists') {
        setError('inss', { type: 'inssAlreadyExists' });
        scrollTo('personalInfo');
        return false;
      }

      // if number success, meaning employee create went well
      return typeof response === 'number' && !isNaN(response);
    },
    [setError],
  );

  const changeLanguage = useCallback(
    (lang: string) => {
      setValue('languageId', `${lang}`);
      const userLang = languageOptions[toNumber(lang) - 1].label;
      i18next.changeLanguage(userLang);
    },
    [setValue, languageOptions, i18next],
  );

  const onError = (err, e) => {
    console.log(err);
    console.log(errors);
    let errorTab = null;
    if (isPersonalInfoError(errors) || inssError) {
      errorTab = 'personalInfo';
    } else if (isContactInfoError(errors)) {
      errorTab = 'contactInfo';
    } else if (isWorkingInfoError(errors)) {
      errorTab = 'workingInfo';
    } else if (isAccountError(errors)) {
      errorTab = 'account';
    } else if (isEmploymentError(errors)) {
      errorTab = 'employment';
    }
    if (errorTab) {
      scrollTo(errorTab);
    }
  };

  const checkIdPeriodAndSetOffErorrs = (
    startDate: Date,
    endDate: Date,
    idUpload: string,
    idUploadBack: string,
  ): boolean => {
    if (idUploadBack === null || idUpload === null) {
      return true;
    }
    const idPeriodIsValid = isIdPeriodValid(startDate, endDate, true);
    if (!idPeriodIsValid) {
      setError('idPeriodInvalid', { type: 'idPeriodInvalid' });
    } else {
      clearErrors('idPeriodInvalid');
    }
    return idPeriodIsValid;
  };

  const uploadStudentDeclaration = (idFromSaveResponse: number): void => {
    const documents: Document[] = [];
    documents.push({
      ...proofOfInscription,
      periodStart: treatAsUTC(watchAcademicPeriodStart),
      periodEnd: treatAsUTC(watchAcademicPeriodEnd),
      employeeId: idFromSaveResponse,
    });
    dispatch(
      saveDocumentsAction({
        companyId,
        documents: fromDocumentToDocumentToSend(documents),
      }),
    );
  };

  const uploadAtOnboarding = (idFromSaveResponse: string, companyGuid: string): void => {
    const document: DocumentToSend = {
      file: proofOfInscription.file,
      category: proofOfInscription.type.toString(),
      periodStart: dayjs(new Date(treatAsUTC(watchAcademicPeriodStart))).format('DD/MM/YYYY'),
      periodEnd: dayjs(new Date(treatAsUTC(watchAcademicPeriodEnd))).format('DD/MM/YYYY'),
      employeeId: idFromSaveResponse,
    };
    dispatch(
      uploadStudentInscription({
        document,
        companyGuid,
      }),
    );
  };

  /**
   * "No override" has no payload representation - null is a deliberate no-op server-side - so
   * clearing an existing override is its own call, made only once the save itself succeeded.
   *
   * This modal routes admins and employees to different save handlers, so the rule lives here rather
   * than inline: a handler that forgets to call it is easier to spot than one that quietly omits the
   * condition. Self-guarding, so calling it on a path that can never clear anything is harmless.
   */
  const clearMfaOverrideIfSelected = async (selected: MfaRequiredOverride | undefined) => {
    if (employee?.userId && shouldClearMfaOverride(employee?.mfaRequiredOverride, selected)) {
      await dispatch(deleteMfaOverrideAction({ userId: employee.userId }));
    }
  };

  // approving or marking not eligible is the same save with a decision flag: the API only honours it for a SuperAdmin
  const saveEmployee = async (decision?: 'approve' | 'notEligible') => {
    const values = getValues();
    values.approve = decision === 'approve';
    values.notEligible = decision === 'notEligible';

    if (employeeBic) {
      values.bic = employeeBic;
    }

    if (!values.companyLocationIds) values.companyLocationIds = [];

    values.inss = values.inss.split('.').join('').split('-').join('');
    let validInss = true;
    if (values.dateOfBirth) {
      validInss = isINSSValid(values.inss, values.gender);
    }
    setInssError(!validInss);

    let isValidResponse = true;

    if (!validInss) {
      setError('inss', { type: 'invalidINSS' });
      return;
    }
    clearErrors('inss');

    const idPeriodIsValid = checkIdPeriodAndSetOffErorrs(
      values.idPeriodStart,
      values.idPeriodEnd,
      values.idUpload,
      values.idUploadBack,
    );

    if (!idPeriodIsValid) {
      return;
    }

    if (!values.retired) {
      values.retiredDate = null;
    }

    // Only a SuperAdmin that is not impersonating may write this, and only when it actually changed.
    // Applied before the branch so create and update both go through it.
    const selectedMfaOverride = canWriteMfaOverride ? values.mfaRequiredOverride : undefined;
    values.mfaRequiredOverride = getMfaOverrideToSend(employee?.mfaRequiredOverride, selectedMfaOverride);

    if (values.id === 0) {
      await dispatch(
        saveEmployeeAction({
          employee: {
            ...values,
            address: {
              ...values.address,
              busNumber: values.address.busNumber === null ? '' : values.address.busNumber,
            },
            stayAddress: {
              ...values.stayAddress,
              busNumber: values.stayAddress.busNumber === null ? '' : values.stayAddress.busNumber,
            },
          },
          companyId,
        }),
      ).then(response => {
        isValidResponse = handleRequestResponse(response.payload);
        if (isValidResponse && proofOfInscription) {
          uploadStudentDeclaration(Number(response.payload));
        }
      });
    } else {
      await dispatch(
        updateEmployeeAction({
          employee: values,
          companyId: pendingEmployeeCompanyId || companyId,
        }),
      ).then(response => {
        isValidResponse = handleRequestResponse(response.payload);
        if (!isValidResponse) {
          return;
        }
        if (newDocToAdd) {
          uploadStudentDeclaration(Number(response.payload));
        } else if (docToUpdate) {
          dispatch(
            updateStudentDeclarationAction({
              companyId,
              employeeId,
              academicPeriodStart: treatAsUTC(watchAcademicPeriodStart),
              academicPeriodEnd: treatAsUTC(watchAcademicPeriodEnd),
            }),
          );
        }
      });

      if (isValidResponse) {
        await clearMfaOverrideIfSelected(selectedMfaOverride);
      }

      if (pendingEmployeeCompanyId) onSavePendingEmployee();
    }

    if (isValidResponse) {
      onClose();
    }
  };

  const onSave: SubmitHandler<IAddEditEmployee> = () => saveEmployee();

  const onSubmit: SubmitHandler<any> = async () => {
    const values = getValues();

    if (values.bic === '') {
      values.bic = employeeBic;
    }

    values.inss = values.inss.split('.').join('').split('-').join('');

    let auxValues = { ...values };
    delete auxValues.pictureBase64;
    delete auxValues.confirmPassword;

    auxValues = {
      ...auxValues,
      languageId: toNumber(auxValues.languageId),
      reasonForEmployment: toNumber(auxValues.reasonForEmployment),
      address: {
        ...auxValues.address,
        streetNumber: toNumber(auxValues.address.streetNumber),
        busNumber: values.address.busNumber === null ? '' : values.address.busNumber,
      },
      stayAddress: auxValues.hasStayAddress
        ? {
            ...auxValues.stayAddress,
            streetNumber: toNumber(auxValues.stayAddress.streetNumber),
            busNumber: values.stayAddress.busNumber === null ? '' : values.stayAddress.busNumber,
          }
        : AddressInitialState,
      modeOfTransport: toNumber(auxValues.modeOfTransport),
      yearsOfExperience: toNumber(auxValues.yearsOfExperience),
      idCardNumber: toNumber(auxValues.idCardNumber),
      dateOfBirth:
        typeof auxValues.dateOfBirth === 'string' ? auxValues.dateOfBirth : auxValues.dateOfBirth.toDateString(),
      idPeriodStart:
        typeof auxValues.idPeriodStart === 'string' || !auxValues.idPeriodStart
          ? auxValues.idPeriodStart
          : auxValues.idPeriodStart.toDateString(),
      idPeriodEnd:
        typeof auxValues.idPeriodEnd === 'string' || !auxValues.idPeriodEnd
          ? auxValues.idPeriodEnd
          : auxValues.idPeriodEnd.toDateString(),
    };

    const idPeriodIsValid = checkIdPeriodAndSetOffErorrs(
      values.idPeriodStart,
      values.idPeriodEnd,
      values.idUpload,
      values.idUploadBack,
    );
    if (!idPeriodIsValid) {
      return;
    }

    // Mirrors onSave. This handler only runs for an employee editing themselves or the onboarding
    // flow, neither of which may write the field, so this resolves to undefined today - it is kept in
    // step with onSave so the two paths cannot drift.
    const selectedMfaOverride = canWriteMfaOverride ? values.mfaRequiredOverride : undefined;

    let isValidRequest = true;
    if (employeeOnboarding) {
      dispatch(
        saveEmployeeAction({
          employee: values,
          companyId: companyToken ?? generalToken,
          invitationToken: inviteToken,
        }),
      ).then(response => {
        if (response.payload && handleRequestResponse(response.payload)) {
          if (newDocToAdd) {
            uploadAtOnboarding(response.payload as string, companyToken ?? generalToken);
          }
          setSuccessfullyOnboarded(true);
          setTimeout(() => {
            window.location.href = '/auth/login';
          }, 1000);
        } else {
          isValidRequest = handleRequestResponse(response.payload);
        }
      });
    } else {
      auxValues.mfaRequiredOverride = getMfaOverrideToSend(employee?.mfaRequiredOverride, selectedMfaOverride);

      await dispatch(updateEmployeeAction({ employee: auxValues, companyId })).then(response => {
        if (response.payload && handleRequestResponse(response.payload)) {
          if (newDocToAdd) {
            uploadStudentDeclaration(isEmployee ? employee.id : employeeId);
          } else if (docToUpdate) {
            dispatch(
              updateStudentDeclarationAction({
                companyId,
                employeeId: isEmployee ? employee.id : employeeId,
                academicPeriodStart: treatAsUTC(watchAcademicPeriodStart),
                academicPeriodEnd: treatAsUTC(watchAcademicPeriodEnd),
              }),
            );
          }
        } else {
          isValidRequest = handleRequestResponse(response.payload);
        }
      });

      if (isValidRequest) {
        await clearMfaOverrideIfSelected(selectedMfaOverride);
        onClose();
      }
    }
  };

  const onChangeCurrentTab = useCallback((event: SyntheticEvent, value: number) => {
    setCurrentTab(value);
  }, []);

  const onChangeCurrentLanguage = useCallback((event: SyntheticEvent, value: number) => {
    setCurrentLanguage(value);
  }, []);

  // validated like a save; the modal stays open with the error when the decision is refused
  const approveEmployee = () => handleSubmit(() => saveEmployee('approve'), onError)();
  const markEmployeeNotEligible = () => handleSubmit(() => saveEmployee('notEligible'), onError)();

  const canApprove = isAdmin && isEdit && employee?.status === EmployeeStatus.RequiresAction;

  useEffect(() => {
    if (busNumberValue === null) setValue('address.busNumber', '');
  }, [busNumberValue, setValue]);

  useEffect(() => {
    if (onboardingDetails?.firstName) {
      setValue('firstName', `${onboardingDetails.firstName}`);
      setValue('lastName', `${onboardingDetails.lastName}`);
      setValue('emailAddress', `${onboardingDetails.emailAddress}`);
      setValue('workerclass', `${workerClassObj.indexOf(onboardingDetails.contractType) + 1}`);
    }

    if (onboardingDetails?.languageId) {
      setValue('languageId', `${onboardingDetails.languageId}`);
      const userLang = languageOptions[toNumber(onboardingDetails.languageId) - 1].label;
      i18next.changeLanguage(userLang);
    }

    if (employeeOnboarding && !onboardingDetails?.languageId) {
      i18next.changeLanguage('nl');
      setValue('languageId', '1');
    }

    if (onboardingDetails?.assignedCompanyLocations) {
      setValue('companyLocationIds', onboardingDetails.assignedCompanyLocations.split(','));
    }
  }, [getValues, i18next, languageOptions, onboardingDetails, employeeOnboarding, setValue]);

  useEffect(() => {
    if (employee && !employeeOnboarding) {
      dispatch(
        getDocumentsAction({
          companyId: toNumber(companyId),
          category: 6,
          employeeId: isEmployee ? employee.id : employeeId,
          startDate: null,
          endDate: null,
          triggerEmployeeDocuments: true,
        }),
      );
    }
  }, [companyId, dispatch, employee, employeeId, isEmployee, employeeOnboarding]);

  useEffect(() => {
    dispatch(clearBicAction());
    dispatch(clearEmployeeAction());
  }, [dispatch]);

  useEffect(() => {
    if (hasSelectedCompany && isEmployee) {
      dispatch(getEmployeeByIdAction({ employeeId: 0, companyId: toNumber(profile?.currentCompany) }));
    } else if (employeeId != null && !Number.isNaN(companyId)) {
      dispatch(getEmployeeByIdAction({ employeeId, companyId }));
    }
  }, [companyId, dispatch, employeeId, isEmployee, profile?.id, hasSelectedCompany, profile?.currentCompany]);

  useEffect(() => {
    if (!employeeOnboarding && employee && employeeDocuments.length > 0) {
      const uploadedDocument = generateEmployeeDocumentForPeriodUpdate(employeeDocuments[0]);
      setProofOfInscription(uploadedDocument);
      setNewDocToAdd(false);
      reset({
        ...employee,
        academicPeriodStart: employeeDocuments[0].periodStart,
        academicPeriodEnd: employeeDocuments[0].periodEnd,
        mfaRequiredOverride: readableMfaOverride,
      });
    } else if (employee) {
      setProofOfInscription(null);
      reset({ ...employee, mfaRequiredOverride: readableMfaOverride });
    }
  }, [employee, employeeDocuments, employeeOnboarding, readableMfaOverride, reset]);

  const navigateToLogin = () => {
    window.location.href = '/auth/login';
  };

  if (isTokenInvalid) {
    return (
      <MessageModal
        message={t(EMPLOYEES__INVATION_TOKEN_INVALID)}
        buttonText={t(GENERAL__GO)}
        open
        onClose={navigateToLogin}
      />
    );
  }

  const isSaveDisabled = () => {
    return (
      // !isValid || isValid check is out of scope, since when pressing the save button, the form field error will scroll to the actual message
      (!isDirty && employeeId > 0) ||
      isSubmitting ||
      errors.idPeriodInvalid !== undefined ||
      errors.inss !== undefined ||
      errors.inss_exists !== undefined ||
      (employeeExists === EmployeeExistsResponse.EXISTS_MATCH && loginFailed) ||
      employeeExists === EmployeeExistsResponse.EXISTS_NO_MATCH
    );
  };

  const gtmEventForSaveButton = () => {
    if (employeeOnboarding) {
      if (onboardingDetails?.firstName) {
        return 'onboarding-personal-invitation';
      }
      return 'onboarding-via-onboarding-link';
    }
    return 'employees-add-new-employee';
  };

  if (employeeOnboarding && onboardingDetails?.hasEmailLinkedToEmployee && !employee) {
    return <OnboardingLogin open={openOnboardingLogin} onClose={() => setOpenOnboardingLogin(false)} />;
  }

  return (
    <Backdrop sx={{ zIndex: '1' }} id="TestMe" open={employeeOnboarding || isEmployee ? false : open}>
      <ModalWrapper open={open} onClose={onClose}>
        <ModalContainer>
          <ModalHeader>
            {!employeeOnboarding && !isEmployee && !isManagerEdit && (
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flexEnd' }}>
                {isEdit && employee && (
                  <ModalTitle>
                    {t(EMPLOYEES__EDIT_EMPLOYEE)}
                    <EmployeeModalMenu
                      employeeName={`${employee.firstName} ${employee.lastName}`}
                      employeeId={employee.id}
                      companyId={companyId}
                    />{' '}
                  </ModalTitle>
                )}
                {!isEdit && <ModalTitle>{t(EMPLOYEES__ADD_NEW_EMPLOYEE)}</ModalTitle>}
                <ModalCloseIcon onClick={onClose}>
                  <CloseIcon fill={theme.palette.secondary.contrastText} />
                </ModalCloseIcon>
              </Box>
            )}

            {isManagerEdit && (
              <>
                <ModalTitle>{t(EMPLOYEES__VIEW_EMPLOYEE)}</ModalTitle>
                <ModalCloseIcon onClick={onClose}>
                  <CloseIcon fill={theme.palette.secondary.contrastText} />
                </ModalCloseIcon>
              </>
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: employeeOnboarding && !onboardingDetails?.languageId ? 'flex-start' : 'center',
              }}
            >
              {employeeOnboarding && !onboardingDetails?.languageId && (
                <Tabs value={currentLanguage} onChange={onChangeCurrentLanguage} sx={{ marginRight: '100px' }}>
                  <TabStyled style={{ minWidth: '5%' }} label="nl" onClick={() => changeLanguage('1')} />
                  <TabStyled style={{ minWidth: '5%' }} label="fr" onClick={() => changeLanguage('2')} />
                  <TabStyled style={{ minWidth: '5%' }} label="en" onClick={() => changeLanguage('3')} />
                </Tabs>
              )}
              <TabsContainer value={currentTab} onChange={onChangeCurrentTab} sx={{ alignSelf: 'center' }}>
                <TabStyled label={t(EMPLOYEES__PERSONAL_INFO)} onClick={() => scrollTo('personalInfo')} />
                <TabStyled label={t(EMPLOYEES__CONTACT_INFO)} onClick={() => scrollTo('contactInfo')} />
                <TabStyled label={t(EMPLOYEES__WORKING_INFO)} onClick={() => scrollTo('workingInfo')} />
                {!isManagerEdit && <TabStyled label={t(EMPLOYEES__ACCOUNT)} onClick={() => scrollTo('account')} />}
                {canSeeEmployment && (
                  <TabStyled label={t(EMPLOYEES__EMPLOYMENT)} onClick={() => scrollTo('employment')} />
                )}
              </TabsContainer>
            </Box>
          </ModalHeader>
          <ContentContainer id="tabsContainer" disableScroll={isBranchDropdownOpen}>
            <Element id="personalInfo" name="personalInfo">
              <PersonalInfoTab
                control={control}
                errors={errors}
                employeeId={employee ? employee.id : 0}
                watch={watch}
                setError={setError}
                clearErrors={clearErrors}
                inssError={inssError}
              />
            </Element>
            <Element id="contactInfo" name="contactInfo">
              <ContactInfoTab
                control={control}
                errors={errors}
                employeeId={employee ? employee.id : 0}
                watch={watch}
                setValue={setValue}
                clearErrors={clearErrors}
                employeeExists={employeeExists}
              />
            </Element>
            <Element id="workingInfo" name="workingInfo">
              <WorkingInfoTab
                control={control}
                errors={errors}
                employeeId={employee ? employee.id : 0}
                watch={watch}
                setValue={setValue}
                clearErrors={clearErrors}
                employee={employee}
                onBoardingLocationIds={
                  onboardingDetails ? onboardingDetails.assignedCompanyLocations.split(',').map(Number) : []
                }
                companyId={pendingEmployeeCompanyId || companyId}
                setIsBranchDropdownOpen={setIsBranchDropdownOpen}
              />
            </Element>
            {!isManagerEdit && (
              <Element id="account" name="account">
                <AccountTab
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  getValues={getValues}
                  setError={setError}
                  clearErrors={clearErrors}
                  employeeIdURL={employee ? employee?.idUploadURL : null}
                  employeeIdBackURL={employee ? employee?.idUploadBackURL : null}
                  companyId={companyId}
                  watch={watch}
                  proofOfInscription={proofOfInscription}
                  setProofOfInscription={setProofOfInscription}
                  setNewDocToAdd={setNewDocToAdd}
                  setDocToUpdate={setDocToUpdate}
                  isEdit={isEdit}
                  employeeExists={employeeExists}
                />
              </Element>
            )}
            {canSeeEmployment && (
              <Element id="employment" name="employment">
                <EligibilityAndTerminationDetails
                  companyId={companyId}
                  control={control}
                  errors={errors}
                  watch={watch}
                  getValues={getValues}
                />
              </Element>
            )}
            {successfullyOnboarded && (
              <Alert severity="success" icon={false} variant="filled">
                {t(EMPLOYEES__ADDED_SUCCESSFULLY)}
              </Alert>
            )}
            {employeeError && employeeOnboarding && (
              <Alert severity="error" icon={false} variant="filled">
                {employeeError === 'inss' ||
                employeeError === 'iban' ||
                employeeError === 'default' ||
                employeeError === 'permanent'
                  ? employeeErrorMessages[employeeError]
                  : t(employeeError)}
              </Alert>
            )}
          </ContentContainer>
          {isManagerEdit && (
            <ModalFooter>
              <Box>
                <UPButton
                  text={t(GENERAL__SAVE_BUTTON)}
                  onClick={() => handleSubmit(isEmployee || employeeOnboarding ? onSubmit : onSave, onError)()}
                  disabled={isSaveDisabled()}
                />
              </Box>
              <Box sx={{ marginLeft: '12px' }}>
                <UPButton text={t(GENERAL__CANCEL_BUTTON)} onClick={onClose} outlined />
              </Box>
            </ModalFooter>
          )}
          {!isManagerEdit && (
            <ModalFooter>
              {canApprove && (
                <Box sx={{ marginRight: '12px' }}>
                  <UPButton text={t(ACTION__APPROVE)} onClick={approveEmployee} disabled={isSubmitting} />
                </Box>
              )}
              {canApprove && (
                <Box sx={{ marginRight: '12px' }}>
                  <UPButton
                    text={t(ACTION__NOT_ELIGIBLE)}
                    onClick={markEmployeeNotEligible}
                    disabled={isSubmitting}
                    outlined
                  />
                </Box>
              )}
              <Box>
                <UPButton
                  text={t(GENERAL__SAVE_BUTTON)}
                  onClick={() => handleSubmit(isEmployee || employeeOnboarding ? onSubmit : onSave, onError)()}
                  disabled={isSaveDisabled()}
                  gtmEvent={gtmEventForSaveButton()}
                />
              </Box>
              <Box sx={{ marginLeft: '12px' }}>
                <UPButton text={t(GENERAL__CANCEL_BUTTON)} onClick={onClose} outlined />
              </Box>
            </ModalFooter>
          )}
        </ModalContainer>
      </ModalWrapper>
    </Backdrop>
  );
};

export default AddEditEmployeeModal;
